import { useEffect, useMemo } from 'react';
import { generatePath, useLocation } from 'react-router-dom';
import { useAppDispatch } from '@store';
import {
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import { useLazyGetCustomersExternalAccountsListQuery } from '../../../customers/store/services/customers';
import { filtersSchema, schema } from '../variables/accounts';
import { PspPartner, OrganizationType } from '../../types';
import { ExternalAccount } from '../types';
import formatExternalList from '../helpers/format-external-accounts';
import { PaymentType } from '../../../../store/dictionaries/types';
import {
  clearAccountsState,
  setOpenPayNotificationVisible,
} from '../../store/slices/external-integration';

import ROUTES from '../../../../routes';

let timeout: NodeJS.Timeout;

const useExternalAccounts = ({
  externalAccounts,
  type,
  customerId,
  paymentTypes,
  casp,
  currenciesList,
  pspPartners,
  openPayNotificationVisible,
}: {
  externalAccounts?: ExternalAccount[];
  type?: OrganizationType;
  customerId?: string;
  paymentTypes?: PaymentType[];
  casp?: CASP[];
  currenciesList?: SelectItemProps[];
  pspPartners?: PspPartner[];
  openPayNotificationVisible?: boolean;
}) => {
  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const [getExternalAccountsList, { isLoading, isFetching }] =
    useLazyGetCustomersExternalAccountsListQuery();

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { onFilter, setPage } = useGetList<ExternalAccount>({
    getList: getExternalAccountsList,
    onSortChange,
    onFilterChange,
    schema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const nextExternalAccounts = externalAccounts?.map((item) => {
      const result = { ...item };

      result.url =
        type === OrganizationType.INDIVIDUAL
          ? `${generatePath(
              ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_ITEM,
              { customerId: customerId || '', accountId: result.id },
            )}${search || ''}`
          : `${generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_ITEM, {
              customerId: customerId || '',
              accountId: result.id,
            })}${search || ''}`;

      return result;
    });

    const data = formatExternalList(
      nextExternalAccounts,
      paymentTypes,
      casp,
      currenciesList,
    );

    return data;
  }, [
    externalAccounts,
    paymentTypes,
    filters,
    type,
    customerId,
    search,
    casp,
    currenciesList,
  ]);

  useEffect(() => {
    setValues(
      'pspPartnerId',
      pspPartners?.map((item) => ({
        name: item.companyName,
        value: item.id,
      })) as SelectItemProps[],
    );
  }, [pspPartners, type]);

  useEffect(() => {
    if (
      !externalAccounts &&
      !search &&
      !Object.keys(filters).length &&
      customerId
    ) {
      onFilter({
        organizationId: {
          value: [customerId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [filters, search, externalAccounts, customerId]);

  useEffect(() => {
    if (openPayNotificationVisible && customerId) {
      timeout = setTimeout(() => {
        dispatch(setOpenPayNotificationVisible(false));

        onFilter({
          organizationId: {
            value: [customerId],
            type: SchemaFilterType.STRING,
          },
          active: {
            value: ['ENABLED'],
            type: SchemaFilterType.STRING,
          },
        });
      }, 10000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [openPayNotificationVisible, customerId]);

  useEffect(() => {
    if (
      customerId &&
      !externalAccounts &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length
    ) {
      const filtersValues: any = {
        ...filters,
        organizationId: { value: [customerId], type: SchemaFilterType.STRING },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(filtersValues);
    }
  }, [filters, search, externalAccounts, prevFilters, customerId]);

  useEffect(
    () => () => {
      dispatch(clearAccountsState());
    },
    [],
  );

  return {
    search,
    formattedData,
    onFilter,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    filters,
    setPage,
  };
};

export default useExternalAccounts;
